const COMPANY = {
  name: "Qauntum International",
  address: "Office 107, Churchill Tower, Business Bay, Dubai, UAE",
  email: "info@qauntuminternational.com",
  phone: "+971 4 834 2593",
};

export const LEGAL_DOCS = {
  privacy: {
    slug: "privacy",
    title: "Privacy Policy",
    eyebrow: "Your data, respected",
    updated: "July 2026",
    intro:
      "This Privacy Policy explains how Qauntum International collects, uses, stores and protects your personal information when you visit our website or engage with our services.",
    sections: [
      {
        heading: "Who we are",
        body: [
          `${COMPANY.name} is a house of services headquartered at ${COMPANY.address}, serving clients in the UAE and internationally. We provide digital marketing, advertising, hospitality and IT consulting services.`,
          `For the purposes of applicable data protection laws — including the UAE Federal Decree-Law No. 45 of 2021 (PDPL) — Qauntum International is the data controller of the personal information described in this policy. You can reach us at any time at ${COMPANY.email} or ${COMPANY.phone}.`,
        ],
      },
      {
        heading: "Information we collect",
        body: [
          "When you submit an enquiry through our contact form, we collect your name, email address, telephone number, the service you are interested in, and the content of your message.",
          "We may also collect limited technical information automatically when you browse the site, such as your browser type, device, approximate location and pages visited. This information is aggregated and does not directly identify you.",
        ],
      },
      {
        heading: "How we use your information",
        body: [
          "We use the information you provide to respond to your enquiry, prepare proposals, deliver the services you engage us for, and manage our business relationship with you.",
          "We may also use your contact details to send occasional updates about our services where you have asked to receive them. You may opt out of these communications at any time by replying to any message or writing to us.",
        ],
      },
      {
        heading: "Legal bases for processing",
        body: [
          "We process personal information only where we have a lawful basis to do so: your consent (for example, when you submit the contact form), the performance of a contract or steps taken at your request before entering one, our legitimate interests in operating and improving our business, and compliance with legal obligations.",
        ],
      },
      {
        heading: "How we share information",
        body: [
          "We do not sell, rent or trade your personal information. We share it only with trusted service providers who help us operate — such as website hosting and email delivery providers — under appropriate confidentiality and data protection obligations, or where disclosure is required by law, regulation or legal process.",
        ],
      },
      {
        heading: "International transfers",
        body: [
          "As a Dubai-based firm, your information is processed in the United Arab Emirates and may be processed in other jurisdictions where our service providers operate. Wherever your information is transferred, we take steps to ensure it receives an adequate level of protection consistent with applicable data protection law.",
        ],
      },
      {
        heading: "Data retention",
        body: [
          "We keep enquiry records for no longer than is necessary for the purposes described in this policy — typically up to 24 months after our last correspondence, unless a longer period is required for an ongoing engagement, a legal obligation, or the establishment or defence of legal claims.",
        ],
      },
      {
        heading: "Your rights",
        body: [
          "Depending on your location, you may have the right to access the personal information we hold about you, to have it corrected or deleted, to restrict or object to our processing of it, to data portability, and to withdraw consent at any time where processing is based on consent.",
          `To exercise any of these rights, contact us at ${COMPANY.email}. We will respond within a reasonable timeframe and in accordance with applicable law. You also have the right to lodge a complaint with your local data protection authority.`,
        ],
      },
      {
        heading: "Security",
        body: [
          "We apply appropriate technical and organisational measures to protect your personal information against unauthorised access, loss, alteration or disclosure. No method of transmission over the internet is completely secure, however, and we cannot guarantee absolute security.",
        ],
      },
      {
        heading: "Changes to this policy",
        body: [
          "We may update this Privacy Policy from time to time. The latest version will always be published on this page with its revision date. We encourage you to review it periodically.",
        ],
      },
      {
        heading: "Contact us",
        body: [
          `Questions about this policy or our handling of your information can be directed to ${COMPANY.name}, ${COMPANY.address}, by email at ${COMPANY.email}, or by telephone at ${COMPANY.phone} (Monday to Friday, 10.00 – 5.00).`,
        ],
      },
    ],
  },

  terms: {
    slug: "terms",
    title: "Terms of Service",
    eyebrow: "The fine print, plainly",
    updated: "July 2026",
    intro:
      "These Terms of Service govern your use of the Qauntum International website. By accessing or using this site, you agree to be bound by these terms. If you do not agree, please do not use the site.",
    sections: [
      {
        heading: "About us",
        body: [
          `This website is operated by ${COMPANY.name}, headquartered at ${COMPANY.address}, serving clients in the UAE and internationally. You can contact us at ${COMPANY.email} or ${COMPANY.phone}.`,
        ],
      },
      {
        heading: "Our services",
        body: [
          "Qauntum International provides digital marketing, advertising, hospitality and IT consulting services. The content of this website is provided for general information only and does not constitute an offer, advice, or a binding proposal.",
          "Any engagement for services is governed by a separate written agreement between you and Qauntum International. In the event of any conflict between these terms and a signed services agreement, the services agreement prevails.",
        ],
      },
      {
        heading: "Acceptable use",
        body: [
          "You agree to use this website only for lawful purposes and in a manner that does not infringe the rights of, restrict, or inhibit anyone else's use of the site. You must not attempt to gain unauthorised access to the site, its servers, or any connected systems, nor introduce malicious code, nor scrape or harvest data from the site without our written consent.",
        ],
      },
      {
        heading: "Intellectual property",
        body: [
          "All content on this website — including text, imagery, graphics, the Qauntum name and monogram, and the site's design — is owned by or licensed to Qauntum International and is protected by copyright, trademark and other intellectual property laws.",
          "You may view and print pages for your personal, non-commercial use. Any other reproduction, distribution or use requires our prior written permission. Brand assets are available on our Brand Kit page for approved usage.",
        ],
      },
      {
        heading: "Third-party links and embeds",
        body: [
          "This site may contain links to third-party websites and embedded content, such as maps and social media platforms. We do not control and are not responsible for the content, accuracy or privacy practices of third-party sites. Accessing them is at your own risk and subject to their own terms.",
        ],
      },
      {
        heading: "Disclaimers",
        body: [
          "This website and its content are provided on an “as is” and “as available” basis without warranties of any kind, whether express or implied, including warranties of accuracy, completeness, merchantability or fitness for a particular purpose.",
          "While we take care to keep information current, we do not warrant that the site will be uninterrupted, error-free or free of viruses or other harmful components.",
        ],
      },
      {
        heading: "Limitation of liability",
        body: [
          "To the fullest extent permitted by law, Qauntum International shall not be liable for any indirect, incidental, special, consequential or punitive damages — including loss of profits, data, goodwill or business opportunities — arising out of or in connection with your use of, or inability to use, this website.",
          "Nothing in these terms excludes or limits liability that cannot be excluded or limited under applicable law.",
        ],
      },
      {
        heading: "Indemnity",
        body: [
          "You agree to indemnify and hold harmless Qauntum International, its officers, employees and partners from any claims, liabilities, damages and expenses (including reasonable legal fees) arising from your misuse of this website or breach of these terms.",
        ],
      },
      {
        heading: "Governing law and jurisdiction",
        body: [
          "These terms are governed by and construed in accordance with the laws of the United Arab Emirates as applied in the Emirate of Dubai. Any dispute arising out of or in connection with these terms shall be subject to the exclusive jurisdiction of the courts of Dubai, without prejudice to any mandatory consumer protections that apply to you in your country of residence.",
        ],
      },
      {
        heading: "Changes to these terms",
        body: [
          "We may revise these Terms of Service at any time. The current version will always be published on this page with its revision date. Continued use of the website after changes take effect constitutes acceptance of the revised terms.",
        ],
      },
      {
        heading: "Contact us",
        body: [
          `Questions about these terms can be directed to ${COMPANY.name}, ${COMPANY.address}, by email at ${COMPANY.email}, or by telephone at ${COMPANY.phone} (Monday to Friday, 10.00 – 5.00).`,
        ],
      },
    ],
  },

  cookies: {
    slug: "cookies",
    title: "Cookie Policy",
    eyebrow: "Small files, clear answers",
    updated: "July 2026",
    intro:
      "This Cookie Policy explains what cookies are, how Qauntum International uses them on this website, and how you can control them.",
    sections: [
      {
        heading: "What are cookies",
        body: [
          "Cookies are small text files placed on your device when you visit a website. They are widely used to make websites work efficiently, to remember your preferences, and to provide information to the site owner. Similar technologies, such as local storage and pixels, serve comparable purposes and are covered by this policy.",
        ],
      },
      {
        heading: "How we use cookies",
        body: [
          "This website is designed to use as few cookies as possible. We use them only where they serve a clear purpose:",
          "Strictly necessary cookies — required for the site to function securely and correctly, such as load balancing and session integrity. These cannot be switched off.",
          "Preference storage — your browser may store interface preferences locally so the site renders consistently on return visits.",
          "Embedded content — the map and social links on our site are provided by third parties (such as Google). When you interact with these embeds, the third party may set its own cookies, which are governed by its own policies.",
        ],
      },
      {
        heading: "Analytics",
        body: [
          "If we introduce analytics tools in future to understand aggregate site usage, we will update this policy and, where required by law, request your consent before any non-essential cookies are set.",
        ],
      },
      {
        heading: "Managing cookies",
        body: [
          "Most browsers allow you to view, block or delete cookies through their settings. You can usually find these controls under “Settings”, “Preferences” or “Privacy”. Blocking strictly necessary cookies may affect how the site functions.",
          "To learn more about cookies generally, including how to see what has been set on your device, visit www.allaboutcookies.org.",
        ],
      },
      {
        heading: "Changes to this policy",
        body: [
          "We may update this Cookie Policy to reflect changes in the cookies we use or for legal reasons. The latest version will always be published on this page with its revision date.",
        ],
      },
      {
        heading: "Contact us",
        body: [
          `Questions about our use of cookies can be directed to ${COMPANY.name}, ${COMPANY.address}, by email at ${COMPANY.email}, or by telephone at ${COMPANY.phone} (Monday to Friday, 10.00 – 5.00).`,
        ],
      },
    ],
  },
};
